/* ============================================================================
 * useScrollBlock.ts
 * ============================================================================
 * Hook para bloquear y desbloquear el scroll del body.
 *
 * Responsabilidades:
 * - Bloquear el scroll del documento (html + body overflow: hidden)
 * - Compensar el ancho del scrollbar para evitar layout shift
 * - Restaurar el scroll y padding original al desbloquear
 * - Prevenir doble bloqueo/desbloqueo mediante ref interna
 *
 * Casos de uso:
 * - Diálogos modales (BackgroundDialog, TransferBackgroundDialog, etc.)
 * - Cualquier overlay que requiera bloquear la interacción con el fondo
 *
 * Las funciones blockScroll y allowScroll están envueltas en
 * useCallback para garantizar estabilidad referencial. Sin esto,
 * cada render del componente consumidor genera nuevas referencias,
 * lo que causa bucles infinitos cuando se usan como dependencias
 * de useEffect:
 *
 *   blockScroll nueva ref → effect re-ejecuta → cleanup(abort) →
 *   re-setup → hook setLoading → re-render → nueva ref → ∞
 *
 * Como ambas funciones solo acceden a scroll.current (ref) y APIs
 * del DOM (sin state ni props reactivos), useCallback([]) es seguro.
 *
 * @module hooks/useScrollBlock
 * ========================================================================== */

import { useCallback, useRef } from "react";

export const useScrollBlock = () => {
  const scroll = useRef(false);

  /**
   * Bloquea el scroll del documento.
   *
   * - Establece overflow: hidden en html y body
   * - Compensa el ancho del scrollbar con padding-right
   * - No-op si ya está bloqueado (scroll.current === true)
   * - No-op en SSR (typeof document === "undefined")
   */
  const blockScroll = useCallback(() => {
    if (typeof document === "undefined") return;

    const html = document.documentElement;
    const { body } = document;

    if (!body?.style || scroll.current) return;

    const scrollBarWidth = window.innerWidth - html.clientWidth;
    const bodyPaddingRight =
      parseInt(
        window.getComputedStyle(body).getPropertyValue("padding-right"),
      ) || 0;

    html.style.position = "relative";
    body.style.position = "relative";
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.paddingRight = `${bodyPaddingRight + scrollBarWidth}px`;

    scroll.current = true;
  }, []);

  /**
   * Restaura el scroll del documento.
   *
   * - Limpia los estilos de overflow y padding
   * - No-op si no está bloqueado (scroll.current === false)
   * - No-op en SSR (typeof document === "undefined")
   */
  const allowScroll = useCallback(() => {
    if (typeof document === "undefined") return;

    const html = document.documentElement;
    const { body } = document;

    if (!body?.style || !scroll.current) return;

    html.style.position = "";
    html.style.overflow = "";
    body.style.position = "";
    body.style.overflow = "";
    body.style.paddingRight = "";

    scroll.current = false;
  }, []);

  return [blockScroll, allowScroll] as const;
};
